import { getRoute } from "../core/getRoute";
import { setConfig, getConfig } from "../constants/config";
import { Route } from "./route";
import tokenService from "../../common/tokenService";

/**
 * Main class for interacting with the Symphony Aggregator using ethers v5.
 * Provides methods for routing, token management, and configuration.
 */
export class Symphony {
  /**
   * Creates a new Symphony instance.
   *
   * @param {ethers.Signer} [signer] - Optional ethers v5 signer for executing transactions
   * @param {Object} [options] - Configuration options
   * @param {string} [options.apiUrl] - Symphony API URL
   * @param {number} [options.chainId] - Chain ID (default: 1329 for SEI)
   * @param {string} [options.chainName] - Chain name (default: "sei")
   * @param {string} [options.rpcUrl] - RPC URL
   * @param {Object} [options.tokens] - Token list override
   */
  constructor({ signer = undefined, options = {} } = {}) {
    this.signer = signer;
    setConfig(options);

    // Proactively fetch token list in background to warm the cache
    // This makes getTokenListAsync much faster for subsequent calls
    // Uses existing race condition protection - if user calls getTokenListAsync
    // before this completes, both will resolve from the same promise
    this._warmTokenCache();
  }

  /**
   * Proactively fetches token list to warm the cache during initialization.
   * This is a fire-and-forget operation that doesn't block the constructor.
   * @private
   */
  _warmTokenCache() {
    try {
      const config = getConfig();
      const {
        chainId,
        additionalTokens,
        overrideDefaultTokens,
        nativeAddress,
      } = config;

      console.log("Symphony: Starting background token fetch to warm cache");

      // Start background fetch - don't await to avoid blocking constructor
      tokenService
        .getAsyncTokenList(
          chainId,
          additionalTokens,
          overrideDefaultTokens,
          nativeAddress
        )
        .then(() => {
          console.log(
            "Symphony: Background token fetch completed successfully, cache is warmed"
          );
        })
        .catch((error) => {
          // Silently handle errors - fallback will work when user actually calls getTokenListAsync
          console.warn(
            "Symphony: Background token fetch failed, will fallback when needed:",
            error.message
          );
        });
    } catch (error) {
      // Silently handle any config errors - user will get proper error when calling getTokenListAsync
      console.warn(
        "Symphony: Could not start background token fetch:",
        error.message
      );
    }
  }

  /**
   * Connects a signer to the Symphony instance.
   * @param {ethers.Signer} signer - The ethers v5 signer to connect
   */
  connectSigner(signer) {
    this.signer = signer;
  }

  /**
   * Disconnects the current signer.
   */
  disconnectSigner() {
    this.signer = undefined;
  }

  /**
   * Gets the best swap route for a token pair.
   *
   * @param {string} tokenIn - Input token address ("0x0" for native SEI)
   * @param {string} tokenOut - Output token address
   * @param {string} amountIn - Input amount
   * @param {Object} [options] - Route options
   * @param {boolean} [options.isRaw=false] - If true, amounts are in raw units (wei)
   * @param {number} [options.slippage] - Custom slippage tolerance
   *
   * @returns {Promise<Route>} Route object containing swap details and execution methods
   *
   * @example
   * // Example 1: Get route for SEI -> USDC swap
   * const route = await symphony.getRoute(
   *   "0x0", // SEI
   *   "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
   *   "1.0" // 1 SEI
   * );
   *
   * @example
   * // Example 2: Get route with raw amounts and custom slippage
   * const route = await symphony.getRoute(
   *   "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
   *   "0xdAC17F958D2ee523a2206206994597C13D831ec7", // USDT
   *   "1000000000000000000", // 1 WSEI in wei
   *   {
   *     isRaw: true,
   *     slippage: "1.0" // 1% slippage
   *   }
   * );
   */
  async getRoute(tokenIn, tokenOut, amountIn, options = {}) {
    const defaultOptions = { isRaw: false };
    const mergedOptions = { ...defaultOptions, ...options };
    const routePayload = await getRoute(
      tokenIn,
      tokenOut,
      amountIn,
      mergedOptions
    );
    return new Route(routePayload, () => this.signer, mergedOptions);
  }

  /**
   * Gets the list of supported tokens (synchronous).
   * Uses static token list with user configuration applied.
   * @deprecated This method uses a static token list. Use `getTokenListAsync` for live data.
   * @returns {Object} Map of token addresses to token metadata
   */
  getTokenList() {
    const config = getConfig();
    // Use tokenService to get static token list with merging logic
    const tokens = tokenService.getStaticTokenList(
      config.chainId,
      config.additionalTokens || {},
      config.overrideDefaultTokens || false,
      config.nativeAddress
    );
    return { ...tokens };
  }

  /**
   * Gets the token list asynchronously with user configuration applied.
   * This method fetches tokens from API with fallback to static tokens,
   * and respects additionalTokens and overrideDefaultTokens configuration.
   *
   * @returns {Promise<Object>} Token list with addresses as keys
   */
  async getTokenListAsync() {
    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    return tokens;
  }

  /**
   * Checks if a token is listed in the static token list (synchronous).
   * Uses static token list with user configuration applied.
   *
   * @deprecated This method uses a static token list. Use `isTokenListedAsync` for live data.
   * @param {string} tokenAddress - Token address to check
   * @returns {boolean} True if token is listed, false otherwise
   */
  isTokenListed(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x"))
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );

    const config = getConfig();
    // Use tokenService to get static token list with merging logic
    const tokens = tokenService.getStaticTokenList(
      config.chainId,
      config.additionalTokens || {},
      config.overrideDefaultTokens || false,
      config.nativeAddress
    );

    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  /**
   * Checks if a token is listed in the async token list (asynchronous).
   * Uses live token data from API with fallback to static tokens.
   * Respects additionalTokens and overrideDefaultTokens configuration.
   *
   * @param {string} tokenAddress - Token address to check
   * @returns {Promise<boolean>} Promise that resolves to true if token is listed, false otherwise
   *
   * @example
   * const symphony = new Symphony();
   * const isListed = await symphony.isTokenListedAsync("0x1234...");
   * console.log(isListed); // true or false
   */
  async isTokenListedAsync(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x"))
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );

    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    // Get updated token list with user's configuration applied
    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  /**
   * Gets the current configuration.
   * @returns {Object} Current configuration object
   */
  getConfig() {
    return getConfig();
  }

  /**
   * Updates the configuration.
   * @param {Object} options - New configuration options
   */
  setConfig(options) {
    setConfig(options);
  }
}
